import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../input/NumericInput";

export interface Period {
  from: string;
  to: string;
}

export const EMPTY_PERIOD: Period = { from: "", to: "" };

const SEARCH_DELAY_MS = 300;

export const LowestValueInput = ({
  label,
  value,
  isPercentage,
  onChange,
}: {
  label: string;
  value: number | null;
  isPercentage: boolean;
  onChange: (value: number | null) => void;
}) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState(value);
  const latestOnChange = useRef(onChange);
  latestOnChange.current = onChange;
  const settle = useMemo(() => debounce((next: number | null) => latestOnChange.current(next), SEARCH_DELAY_MS), []);
  useEffect(() => setTyped(value), [value]);

  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <InputGroup size="sm">
        <NumericInput
          size="sm"
          placeholder={t("At least") ?? undefined}
          numericValue={typed}
          onNumericChange={(next) => {
            setTyped(next);
            settle(next);
          }}
        />
        {isPercentage && <InputRightAddon>%</InputRightAddon>}
      </InputGroup>
    </FormControl>
  );
};

export const PeriodInput = ({
  label,
  period,
  onChange,
}: {
  label: string;
  period: Period;
  onChange: (period: Period) => void;
}) => {
  const { t } = useTranslation();
  const fromId = useId();
  const toId = useId();

  return (
    <FormControl as="fieldset" isInvalid={isReversed(period)}>
      <FormLabel as="legend" fontSize="sm">
        {label}
      </FormLabel>
      <Flex gap="2" direction={{ base: "column", sm: "row" }}>
        <Box flex="1" minW="0">
          <FormLabel htmlFor={fromId} fontSize="xs" color="smMuted" mb="0">
            {t("From")}
          </FormLabel>
          <Input
            id={fromId}
            type="date"
            size="sm"
            rounded="md"
            value={period.from}
            max={period.to || undefined}
            onChange={(e) => onChange({ ...period, from: e.target.value })}
          />
        </Box>
        <Box flex="1" minW="0">
          <FormLabel htmlFor={toId} fontSize="xs" color="smMuted" mb="0">
            {t("To")}
          </FormLabel>
          <Input
            id={toId}
            type="date"
            size="sm"
            rounded="md"
            value={period.to}
            min={period.from || undefined}
            onChange={(e) => onChange({ ...period, to: e.target.value })}
          />
        </Box>
      </Flex>
      <FormErrorMessage>{t("«To» must be the same day as «From» or later")}</FormErrorMessage>
    </FormControl>
  );
};

export const isReversed = (period: Period): boolean => Boolean(period.from && period.to && period.from > period.to);
